import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, of } from 'rxjs';
import { AuthState } from 'app/core/state/auth.state';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { buildPracticeDetailFuseTree } from 'app/modules/tma/main-dashboards/practice-detail-nav';
import { NavStructureService }         from 'app/modules/tma/main-dashboards/nav-structure.service';
import { APP_VERSION, APP_VERSION_FULL_LABEL } from 'app/core/version/app-version';

/**
 * ============================================================================
 * NAVIGATION SERVICE - ROLE-BASED ACCESS CONTROL
 * ============================================================================
 *
 * This service builds navigation menus based on user roles.
 *
 * ROLE → NAVIGATION MAPPING:
 * - SuperAdmin: Admin menu only (with Organizations)
 * - SystemAdmin: Admin menu only (without Organizations)
 * - OrganizationAdmin: Analytics dashboards only
 * - AnalyticsAdmin: Analytics dashboards only
 * - Development: Analytics + Development Zone
 * - Demo: Analytics + Demo Experience
 *
 * HOW TO ADD NEW NAV ITEMS:
 * 1. Add the item to _staticNavigation below
 * 2. Add the nav item ID to NAV_SECTIONS in roles.model.ts
 * 3. The item will automatically be shown/hidden based on roles
 *
 * Users with NO roles see NOTHING.
 * Users with MULTIPLE roles see the union of all their allowed items.
 * ============================================================================
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _authState     = inject(AuthState);
    private _navStructure  = inject(NavStructureService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // =========================================================================
    // STATIC NAVIGATION DATA
    // =========================================================================
    // The Practice Detail subtree is now Cosmos-driven via NavStructureService
    // (NAV_STRUCTURE_V1~practice-detail). Other nav entries (entity dashboards,
    // visit view, etc.) remain TS-literal until their own structures move to
    // Cosmos. Edit `practice-detail-nav.ts` for fallback / dev defaults; edit
    // the Cosmos doc for production.
    //
    // To add a new nav item OUTSIDE Practice Detail:
    //   1. Add it here in `getStaticNavigation()` under the appropriate section
    //   2. Add its ID to NAV_SECTIONS in roles.model.ts under the correct category
    // =========================================================================
    private getStaticNavigation(): Navigation {
        const cats = this._navStructure.get();
        return {
            default: [
                // =============================================
                // TRIAD MEDICAL ANALYTICS — User-facing dashboards
                // =============================================
                {
                    id: 'mvp-beta',
                    title: 'Triad Medical Analytics',
                    subtitle: APP_VERSION_FULL_LABEL,
                    type: 'group',
                    icon: 'tma:tminsights',
                    children: [
                        { id: 'mvp-tma-dashboard',     title: 'TMA Dashboard',     type: 'basic', icon: 'tma:tma-dashboard',      link: '/main-dashboard' },
                        { id: 'mvp-user-dashboard',    title: 'User Dashboard',    type: 'basic', icon: 'tma:user-dashboard',     link: '/user-dashboard' },
                        { id: 'mvp-practice-overview', title: 'Practice Overview', type: 'basic', icon: 'tma:practice-overview',  link: '/practice-overview' },
                        { id: 'mvp-practice-trends',   title: 'Practice Trends',   type: 'basic', icon: 'tma:encounter-flow',     link: '/practice-trends' },
                        // Practice Detail — Cosmos-driven (NAV_STRUCTURE_V1~practice-detail)
                        buildPracticeDetailFuseTree('mvp', cats),
                        { id: 'mvp-data-explorer',     title: 'TMA Data Explorer',     type: 'basic', icon: 'tma:data-extraction',    link: '/data-extraction-v3' },
                        { id: 'mvp-tma-pivot',         title: 'TMA Pivot',         type: 'basic', icon: 'tma:tminsights',         link: '/tma-pivot' },
                        { id: 'mvp-tm-ai-build',       title: 'TMA Insights',      type: 'basic', icon: 'tma:tmai',               link: '/tm-ai/build-with-me' },
                        { id: 'mvp-visit-view',        title: 'Visit View',        type: 'basic', icon: 'tma:dm-encounter',       link: '/visit-view' },
                        { id: 'medent-reports',        title: 'Medent Reports',    type: 'basic', icon: 'tma:dm-payment',         link: '/medent-reports' },
                        // Appointments module — summary dashboard, worklist queues, rebooking reports
                        {
                            id: 'mvp-appointments',
                            title: 'Appointments',
                            type: 'collapsable',
                            icon: 'tma:patient-retention',
                            badge: { title: 'New', classes: 'px-1.5 rounded-full text-xs font-semibold bg-brand text-white' },
                            children: [
                                { id: 'mvp-appt-dashboard', title: 'Appointment Dashboard',  type: 'basic', icon: 'tma:encounter-analytics', link: '/appointments/dashboard' },
                                { id: 'mvp-worklist-hub',   title: 'Appointment Management', type: 'basic', icon: 'tma:patient-retention',   link: '/appointments/worklists' },
                                { id: 'mvp-appt-reports',   title: 'Rebooking Reports',      type: 'basic', icon: 'tma:patient-outcomes',    link: '/appointments/reports' },
                            ],
                        },
                        // Accounting — entry-date GL ledger + unapplied-cash deposits
                        {
                            id: 'mvp-accounting',
                            title: 'Accounting',
                            type: 'collapsable',
                            icon: 'tma:dm-payment',
                            children: [
                                { id: 'mvp-acct-explorer', title: 'Accounting Explorer', type: 'basic', icon: 'tma:data-extraction', link: '/accounting/explorer' },
                                { id: 'mvp-acct-pivot',    title: 'TMA Accounting Pivot',type: 'basic', icon: 'tma:tminsights',       link: '/accounting/pivot' },
                                { id: 'mvp-acct-deposits', title: 'Deposits',            type: 'basic', icon: 'tma:dm-payment',      link: '/accounting/deposits' },
                                { id: 'mvp-acct-visit',    title: 'Visit Accounting',    type: 'basic', icon: 'tma:dm-encounter',    link: '/accounting/visit' },
                                {
                                    id: 'mvp-acct-cost',
                                    title: 'Cost & Profitability',
                                    type: 'collapsable',
                                    icon: 'tma:revenue-enhancement',
                                    children: [
                                        { id: 'mvp-acct-job-costing',  title: 'Job Costing',          type: 'basic', icon: 'tma:medicare-rates',          link: '/accounting/job-costing' },
                                        { id: 'mvp-acct-margin',       title: 'Contribution Margin',  type: 'basic', icon: 'tma:revenue-enhancement',     link: '/accounting/margin' },
                                        { id: 'mvp-acct-productivity', title: 'Provider Productivity',type: 'basic', icon: 'tma:dm-rendering-physician',  link: '/accounting/productivity' },
                                        { id: 'mvp-acct-cost-model',   title: 'Cost Model',           type: 'basic', icon: 'tma:dm-chargemaster',         link: '/accounting/settings/cost-model' },
                                    ]
                                },
                            ]
                        },
                        // General Dashboards
                        {
                            id: 'mvp-general-dashboards',
                            title: 'General Dashboards',
                            type: 'collapsable',
                            icon: 'tma:analytics-dashboard',
                            children: [
                                { id: 'mvp-gd-patient-general',     title: 'Patient General',     type: 'basic', icon: 'tma:dm-patient',              link: '/tmamdv1/patient-general' },
                                { id: 'mvp-gd-patient-finance',     title: 'Patient Finance',     type: 'basic', icon: 'tma:revenue-enhancement',     link: '/tmamdv1/patient-finance' },
                                { id: 'mvp-gd-referring-physician', title: 'Referring Physician', type: 'basic', icon: 'tma:dm-referring-physician',  link: '/tmamdv1/referring-physician' },
                                { id: 'mvp-gd-rendering-physician', title: 'Rendering Physician', type: 'basic', icon: 'tma:dm-rendering-physician',  link: '/tmamdv1/rendering-physician' },
                                { id: 'mvp-gd-facility',            title: 'Facility',            type: 'basic', icon: 'tma:dm-facility',             link: '/tmamdv1/facility' },
                                { id: 'mvp-gd-payer',               title: 'Payer',               type: 'basic', icon: 'tma:dm-payer',                link: '/tmamdv1/payer' },
                                { id: 'mvp-gd-diagnosis',           title: 'Diagnosis',           type: 'basic', icon: 'tma:dm-icd10',                link: '/tmamdv1/diagnosis' },
                                { id: 'mvp-gd-cpt',                 title: 'CPT',                 type: 'basic', icon: 'tma:dm-cpt-code',             link: '/tmamdv1/cpt' },
                            ]
                        },
                        // Entity Spotlights — pick one entity, drill into its V4 spotlight dashboard
                        {
                            id: 'mvp-entity-spotlights',
                            title: 'Entity Spotlights',
                            type: 'collapsable',
                            icon: 'tma:tminsights',
                            children: [
                                { id: 'mvp-sp-patient-general',     title: 'Patient General',     type: 'basic', icon: 'tma:dm-patient',              link: '/spotlight/patient-general' },
                                { id: 'mvp-sp-patient-finance',     title: 'Patient Finance',     type: 'basic', icon: 'tma:revenue-enhancement',     link: '/spotlight/patient-finance' },
                                { id: 'mvp-sp-referring-physician', title: 'Referring Physician', type: 'basic', icon: 'tma:dm-referring-physician',  link: '/spotlight/referring-physician' },
                                { id: 'mvp-sp-rendering-physician', title: 'Rendering Physician', type: 'basic', icon: 'tma:dm-rendering-physician',  link: '/spotlight/rendering-physician' },
                                { id: 'mvp-sp-facility',            title: 'Facility',            type: 'basic', icon: 'tma:dm-facility',             link: '/spotlight/facility' },
                                { id: 'mvp-sp-payer',               title: 'Payer',               type: 'basic', icon: 'tma:dm-payer',                link: '/spotlight/payer' },
                                { id: 'mvp-sp-diagnosis',           title: 'Diagnosis',           type: 'basic', icon: 'tma:dm-icd10',                link: '/spotlight/diagnosis' },
                                { id: 'mvp-sp-cpt',                 title: 'CPT',                 type: 'basic', icon: 'tma:dm-cpt-code',             link: '/spotlight/cpt' },
                            ]
                        },
                    ]
                }
                // ADMIN MENU built dynamically in buildAdminMenu()
            ],
            compact: [], futuristic: [], horizontal: []
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data filtered by user roles
     */
    get(): Observable<Navigation> {
        const navigation = this.buildNavigation();
        this._navigation.next(navigation);
        return of(navigation);
    }

    /**
     * Rebuild and re-emit navigation (e.g. after nav mode toggle)
     */
    refreshNavigation(): void {
        const navigation = this.buildNavigation();
        this._navigation.next(navigation);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods - Role-based navigation building
    // -----------------------------------------------------------------------------------------------------

    /**
     * Build navigation based on user roles
     * Users with no roles get empty navigation
     */
    private buildNavigation(): Navigation {
        const userRoles = this._authState.userRoles();
        const navMode = this._authState.navMode();

        // No roles = no navigation
        if (!userRoles || userRoles.length === 0) {
            return { default: [], compact: [], futuristic: [], horizontal: [] };
        }

        let defaultNav: FuseNavigationItem[] = [];

        if (navMode === 'admin' && this._authState.hasAdminAccess()) {
            // Admin mode — show only admin menu
            const adminMenu = this.buildAdminMenu();
            defaultNav = [adminMenu];
        } else {
            // User mode — rebuild from latest Cosmos-driven structure each call
            const staticNav = this.getStaticNavigation();
            defaultNav = JSON.parse(JSON.stringify(staticNav.default));
            defaultNav = this.filterByRoles(defaultNav, userRoles);
        }

        // Remove empty groups and collapsables
        defaultNav = this.removeEmptyContainers(defaultNav);

        return {
            default: defaultNav,
            compact: [],   // Simplified - only using default
            futuristic: [],
            horizontal: []
        };
    }

    /**
     * Filter navigation items based on user roles
     * Uses NAV_SECTIONS from roles.model.ts to determine access
     */
    private filterByRoles(items: FuseNavigationItem[], userRoles: string[]): FuseNavigationItem[] {
        return items.filter(item => {
            // Check if user can access this nav item
            if (!this._authState.canAccessNavItem(item.id)) {
                return false;
            }

            // Recursively filter children
            if (item.children && item.children.length > 0) {
                item.children = this.filterByRoles(item.children, userRoles);
            }

            return true;
        });
    }

    /**
     * Remove empty groups and collapsables after filtering
     */
    private removeEmptyContainers(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items.filter(item => {
            // Recursively clean children first
            if (item.children && item.children.length > 0) {
                item.children = this.removeEmptyContainers(item.children);
            }

            // Remove empty containers
            if ((item.type === 'group' || item.type === 'collapsable') && item.children) {
                return item.children.length > 0;
            }

            return true;
        });
    }

    /**
     * Single source of truth for TMA Meta Dashboard nav children.
     * Used by both the analytics (tma-custom) and admin nav groups.
     */
    private buildMetaDashboardChildren(idPrefix: string = ''): FuseNavigationItem[] {
        const p = idPrefix ? `${idPrefix}-` : '';
        return [
            // ── Practice Detail — Cosmos-driven (NAV_STRUCTURE_V1~practice-detail) ──
            buildPracticeDetailFuseTree(idPrefix, this._navStructure.get()),
            // ── Key Dashboards ──
            {
                id: `${p}dash-key-finance`,
                title: 'Key Finance',
                type: 'basic',
                icon: 'tma:revenue-enhancement',
                link: '/dashboards/key-finance',
                subtitle: 'Practice-wide revenue scorecard'
            },
            {
                id: `${p}dash-key-ops`,
                title: 'Key Operations',
                type: 'basic',
                icon: 'tma:operations-metrics',
                link: '/dashboards/key-ops',
                subtitle: 'Practice-wide throughput + productivity'
            },
            {
                id: `${p}dash-patient-general`,
                title: 'Patient General',
                type: 'basic',
                icon: 'tma:dm-patient',
                link: '/tmamdv1/patient-general',
                subtitle: 'Per-patient clinical detail'
            },
            {
                id: `${p}dash-patient-finance`,
                title: 'Patient Finance',
                type: 'basic',
                icon: 'tma:revenue-enhancement',
                link: '/tmamdv1/patient-finance',
                subtitle: 'Per-patient financial detail'
            },
            {
                id: `${p}dash-key-referrals`,
                title: 'Key Referrals',
                type: 'basic',
                icon: 'tma:dm-referring-physician',
                link: '/dashboards/key-referrals',
                subtitle: 'Practice-wide referral analysis'
            },
            {
                id: `${p}dash-key-regulation`,
                title: 'Key Regulation',
                type: 'basic',
                icon: 'tma:quality-compliance',
                link: '/dashboards/key-regulation',
                subtitle: 'Coding compliance + audit risk',
                badge: { title: 'Soon', classes: 'text-xs' }
            },
            {
                id: `${p}dash-other-finance`,
                title: 'Other Finance',
                type: 'basic',
                icon: 'tma:revenue-enhancement',
                link: '/dashboards/other-finance',
                subtitle: 'Expected vs actual, AR aging, write-off deep-dive'
            },
            {
                id: `${p}dash-other-ops`,
                title: 'Other Operations',
                type: 'basic',
                icon: 'tma:operations-metrics',
                link: '/dashboards/other-ops',
                subtitle: 'Sites, specialties, needs-review watchlist'
            },
            {
                id: `${p}dash-referring-physician`,
                title: 'Referring Physician',
                type: 'basic',
                icon: 'tma:dm-referring-physician',
                link: '/tmamdv1/referring-physician',
                subtitle: 'Referral revenue + volume per MD'
            },
            {
                id: `${p}dash-rendering-physician`,
                title: 'Rendering Physician',
                type: 'basic',
                icon: 'tma:dm-rendering-physician',
                link: '/tmamdv1/rendering-physician',
                subtitle: 'Provider productivity + revenue'
            },
            {
                id: `${p}dash-facility`,
                title: 'Facility',
                type: 'basic',
                icon: 'tma:dm-facility',
                link: '/tmamdv1/facility',
                subtitle: 'Per-location performance'
            },
            {
                id: `${p}dash-payer`,
                title: 'Payer',
                type: 'basic',
                icon: 'tma:dm-payer',
                link: '/tmamdv1/payer',
                subtitle: 'Reimbursement by payer'
            },
            {
                id: `${p}dash-diagnosis`,
                title: 'Diagnosis',
                type: 'basic',
                icon: 'tma:dm-icd10',
                link: '/tmamdv1/diagnosis',
                subtitle: 'Per-ICD-10 volume + revenue'
            },
            {
                id: `${p}dash-cpt`,
                title: 'CPT',
                type: 'basic',
                icon: 'tma:dm-cpt-code',
                link: '/tmamdv1/cpt',
                subtitle: 'Per-CPT volume + revenue'
            },
            {
                id: `${p}dash-new-patient`,
                title: 'New Patient Analytics',
                type: 'basic',
                icon: 'tma:new-patient-analytics',
                link: '/dashboards/new-patient',
                subtitle: 'New-patient acquisition (CPT 9920x)'
            }
        ];
    }

    /**
     * Build admin menu for SuperAdmin/SystemAdmin users
     * Organizations only visible to SuperAdmin
     */
    private buildAdminMenu(): FuseNavigationItem {
        const children: FuseNavigationItem[] = [];

        children.push(
            // Dashboard
            {
                id: 'admin-dashboard',
                title: 'Dashboard',
                type: 'basic',
                icon: 'heroicons_outline:home',
                link: '/admin/dashboard',
                subtitle: 'Admin overview'
            },

            // Data Explorer — direct admin-side access to /data-extraction-v3
            {
                id: 'admin-data-explorer',
                title: 'TMA Data Explorer',
                type: 'basic',
                icon: 'tma:data-extraction',
                link: '/data-extraction-v3',
                subtitle: 'Tab-based data extraction (silver entities)'
            },

            // Charge Master — consolidated charge/fee-schedule reporting.
            // Dashboard first, then the fee-tools suite, then links to the
            // standalone charge-master analytics pages.
            {
                id: 'admin-fee-tools',
                title: 'Charge Master',
                type: 'collapsable',
                icon: 'tma:dm-chargemaster',
                subtitle: 'Charge levels, fee schedules, payment integrity',
                children: [
                    {
                        id: 'admin-cm-dashboard',
                        title: 'Dashboard',
                        type: 'basic',
                        icon: 'heroicons_outline:chart-bar',
                        link: '/admin/charge-master/dashboard',
                        subtitle: 'Paid-vs-billed flags + charge vs fee schedule headroom'
                    },
                    {
                        id: 'admin-fs-status',
                        title: 'Fee Schedule Status',
                        type: 'basic',
                        icon: 'heroicons_outline:clipboard-document-check',
                        link: '/admin/fee-schedule-management',
                        subtitle: 'Per-payer schedule status table + maintenance tools'
                    },
                    {
                        id: 'admin-fs-explorer',
                        title: 'Fee Schedule Explorer',
                        type: 'basic',
                        icon: 'heroicons_outline:table-cells',
                        link: '/admin/fee-tools/explorer',
                        subtitle: 'Browse Payer Schedules / Charge Master / Medicare / Inferred / Collection %'
                    },
                    {
                        id: 'admin-fs-uploads',
                        title: 'Upload Status',
                        type: 'basic',
                        icon: 'heroicons_outline:cloud-arrow-up',
                        link: '/admin/fee-tools/uploads',
                        subtitle: 'All uploads + coverage + per-payer summary'
                    },
                    {
                        id: 'admin-fs-ai-ingest',
                        title: 'AI Fee Schedule Ingestion',
                        type: 'basic',
                        icon: 'tma:tmai',
                        link: '/admin/fee-tools/ai-ingest',
                        subtitle: 'Drop a payer file, AI extracts the schedule',
                        badge: { title: 'Soon', classes: 'text-xs' }
                    },
                    {
                        id: 'admin-fs-cpt-compare',
                        title: 'CPT Rate Compare',
                        type: 'basic',
                        icon: 'tma:dm-cpt-code',
                        link: '/admin/fee-tools/cpt-compare',
                        subtitle: 'Side-by-side rates for any CPT/mod/site/provider combo'
                    },
                    {
                        id: 'admin-fs-visit-analyzer',
                        title: 'Visit Rate Analyzer',
                        type: 'basic',
                        icon: 'tma:dm-encounter',
                        link: '/admin/fee-tools/visit-analyzer',
                        subtitle: 'Per-line rate cascade + collection outcome + AI summary'
                    },
                    {
                        id: 'admin-cm-review',
                        title: 'Charge Master Review',
                        type: 'basic',
                        icon: 'tma:dm-chargemaster',
                        link: '/dashboardv2/chargemaster',
                        subtitle: 'CPT fees vs Medicare benchmarks'
                    },
                    {
                        id: 'admin-cm-fs-intelligence',
                        title: 'Fee Schedule Intelligence',
                        type: 'basic',
                        icon: 'heroicons_outline:light-bulb',
                        link: '/fee-schedule-intelligence',
                        subtitle: 'Rate matrix, payer profiles, Medicare trends, gap analysis'
                    },
                    {
                        id: 'admin-cm-fs-extraction',
                        title: 'Fee Schedule Extraction',
                        type: 'basic',
                        icon: 'tma:data-extraction',
                        link: '/fee-schedule-extraction',
                        subtitle: 'Extraction-style fee schedule browser'
                    },
                ]
            },

            {
                id: 'admin-pipeline-console',
                title: 'Pipeline Console',
                type: 'basic',
                icon: 'tma:pipeline',
                link: '/admin/pipeline-console',
                subtitle: 'Unified daily + initial load progress'
            },
            // buildAdminMenu() is not filtered by NAV_SECTIONS → gate SuperAdmin-only items here.
            ...(this._authState.isSuperAdmin() ? [{
                id: 'admin-outreach',
                title: 'Outreach',
                type: 'basic' as const,
                icon: 'campaign',
                link: '/admin/outreach',
                subtitle: 'Prospect emails + funnel'
            }] : []),
            ...(this._authState.isSuperAdmin() ? [{
                id: 'admin-report-subscriptions',
                title: 'Report Subscriptions',
                type: 'basic' as const,
                icon: 'tma:report-subscription',
                link: '/admin/report-subscriptions',
                subtitle: 'Scheduled emails'
            }] : []),
            {
                id: 'admin-data-injest-createandseed',
                title: 'Create & Seed Org',
                type: 'basic',
                icon: 'tma:dm-encounter',
                link: '/admin/data-injest-createandseed',
                subtitle: 'New org → SFTP → stage → Databricks initial'
            },
            {
                id: 'admin-data-injest-incremental',
                title: 'Incremental Run',
                type: 'basic',
                icon: 'tma:dm-encounter',
                link: '/admin/data-injest-incremental',
                subtitle: 'Fetch increments → Databricks nb4 → gold'
            },
            {
                id: 'admin-demo-generator',
                title: 'Demo Generator',
                type: 'basic',
                icon: 'tma:dm-encounter',
                link: '/admin/demo-generator',
                subtitle: 'Reduce Maxim / synthesize a practice → off-Databricks pipeline'
            },
            {
                id: 'admin-demo-builder',
                title: 'Demo Builder',
                type: 'basic',
                icon: 'tma:dm-encounter',
                link: '/admin/demo-builder',
                subtitle: 'Synthesize a practice → C# Bronze+brosil → Databricks Silver → gold'
            },
            {
                id: 'admin-medent-reports',
                title: 'Medent Reports',
                type: 'basic',
                icon: 'tma:dm-payment',
                link: '/admin/medent-reports',
                subtitle: 'Doctor + account money reports to tie out vs exported Maxim/Medent data'
            },
            {
                id: 'admin-pipeline-verify',
                title: 'Pipeline Verification',
                type: 'basic',
                icon: 'heroicons_outline:shield-check',
                link: '/admin/pipeline-verify',
                subtitle: 'Raw Medent zip vs silver ledger — every delivered dollar accounted for'
            },
            {
                id: 'admin-data-validation',
                title: 'Data Validation',
                type: 'basic',
                icon: 'heroicons_outline:shield-check',
                link: '/admin/data-validation',
                subtitle: '45 automated data checks'
            },
            {
                id: 'admin-widget-query-tester',
                title: 'Widget Query Tester',
                type: 'basic',
                icon: 'heroicons_outline:beaker',
                link: '/admin/widget-query-tester',
                subtitle: 'Run + verify every Practice Overview widget query'
            },
            {
                id: 'admin-widget-gold-builder',
                title: 'Widget Gold Builder',
                type: 'basic',
                icon: 'heroicons_outline:squares-2x2',
                link: '/admin/widget-gold-builder',
                subtitle: 'Materialize widget gold cache (matrix UI)'
            },
            {
                id: 'admin-tm-ai-v2',
                title: 'TMA Insights V2',
                type: 'basic',
                icon: 'tma:tmai',
                link: '/tm-ai/insights-v2',
                subtitle: 'Staged AI chat — appointments-focused, confirms before it runs'
            },
            {
                id: 'admin-tm-ai-audit',
                title: 'TMA-Insights Audit',
                type: 'basic',
                icon: 'tma:tmai',
                link: '/tm-ai/audit-review',
                subtitle: 'AI turn log — failure categories, raw output, Cosmos errors'
            },

            // (User-facing TMA nav items are in the static navigation above —
            //  admin users see them automatically. No duplication needed here.)

            // Data Explorer
            {
                id: 'data-extraction',
                title: 'TMA Data Explorer',
                type: 'collapsable',
                icon: 'heroicons_outline:arrow-down-tray',
                subtitle: 'Export and query data',
                children: [
                    {
                        id: 'admin-silver-extraction',
                        title: 'Silver Extraction',
                        type: 'basic',
                        icon: 'heroicons_outline:arrow-down-tray',
                        link: '/admin/silver-extraction',
                        subtitle: 'Export silver data as CSV/Excel'
                    },
                    {
                        id: 'admin-silver-extraction-v2',
                        title: 'Silver Extraction V2',
                        type: 'basic',
                        icon: 'heroicons_outline:table-cells',
                        link: '/admin/silver-extraction-v2',
                        subtitle: 'Tab-based extraction with entity filters'
                    },
                    {
                        id: 'admin-report-wizard',
                        title: 'Report Wizard',
                        type: 'basic',
                        icon: 'heroicons_outline:sparkles',
                        link: '/admin/report-wizard',
                        subtitle: 'Guided data analysis & export'
                    },
                    {
                        id: 'admin-gandalf',
                        title: 'Gandalf',
                        type: 'basic',
                        icon: 'heroicons_outline:wrench-screwdriver',
                        link: '/admin/gandalf',
                        subtitle: 'Visual wizard builder'
                    },
                ]
            },

            // User/Org Management
            {
                id: 'user-org-management',
                title: 'User/Org Management',
                type: 'collapsable',
                icon: 'heroicons_outline:user-group',
                subtitle: 'Organizations and users',
                children: [
                    {
                        id: 'admin-organizations',
                        title: 'Organizations',
                        type: 'basic',
                        icon: 'tma:reimbursement-variance',
                        link: '/admin/organizations',
                        subtitle: 'Manage all organizations'
                    },
                    {
                        id: 'admin-users',
                        title: 'User Management',
                        type: 'basic',
                        icon: 'heroicons_outline:users',
                        link: '/admin/users',
                        subtitle: 'Manage users and roles'
                    },
                ]
            },

            // Goals — tenant-scoped targets for key metrics
            {
                id: 'admin-goals-group',
                title: 'Goals',
                type: 'collapsable',
                icon: 'tma:revenue-enhancement',
                subtitle: 'Targets for key metrics',
                children: [
                    {
                        id: 'admin-goals-overview',
                        title: 'Goals Overview',
                        type: 'basic',
                        icon: 'tma:revenue-enhancement',
                        link: '/admin/goals',
                        subtitle: 'Scoreboard of all tenant goals'
                    },
                    {
                        id: 'admin-goals-configure',
                        title: 'Configure Goals',
                        type: 'basic',
                        icon: 'heroicons_outline:adjustments-horizontal',
                        link: '/admin/goals/configure',
                        subtitle: 'Edit targets + thresholds'
                    },
                    {
                        id: 'admin-goals-seed',
                        title: 'Seed Starter Goals',
                        type: 'basic',
                        icon: 'heroicons_outline:sparkles',
                        link: '/admin/goals/seed',
                        subtitle: 'Seed the 14-metric catalog'
                    }
                ]
            },

            // Data Enrichment — single umbrella for classification + late-stage
            // pipeline surfaces. The legacy per-entity pipeline children remain
            // in the Analytics Pipeline group untouched; Data Enrichment adds
            // alternate entries that all land on the same routes.
            // Per plan_data_enrichment_unification.md + user direction 2026-04-22.
            {
                id: 'data-enrichment',
                title: 'Data Enrichment',
                type: 'collapsable',
                icon: 'tma:dm-cpt-code',
                subtitle: 'Classify and enrich entities',
                children: [
                    {
                        id: 'data-enrichment-general',
                        title: 'General Enrichment',
                        type: 'basic',
                        icon: 'tma:dm-cpt-code',
                        link: '/admin/data-enrichment',
                        subtitle: 'Classify all entities on one page',
                    },
                    {
                        id: 'data-enrichment-medicare',
                        title: 'Medicare & Rates',
                        type: 'basic',
                        icon: 'heroicons_outline:currency-dollar',
                        link: '/admin/medicare-rates',
                    },
                    {
                        id: 'data-enrichment-silver-analytics',
                        title: 'Silver Analytics',
                        type: 'basic',
                        icon: 'heroicons_outline:chart-bar',
                        link: '/admin/silver-analytics',
                    },
                    {
                        id: 'data-enrichment-gold-aggregation',
                        title: 'Gold Aggregation',
                        type: 'basic',
                        icon: 'heroicons_outline:chart-bar-square',
                        link: '/admin/gold-aggregation',
                    },
                ]
            },

            // Analytics Pipeline
            {
                id: 'analytics-pipeline',
                title: 'Analytics Pipeline',
                type: 'collapsable',
                icon: 'heroicons_outline:chart-bar-square',
                subtitle: 'End-to-end data pipeline',
                children: [
                    {
                        id: 'admin-context',
                        title: '1. Select Context',
                        type: 'basic',
                        icon: 'heroicons_outline:building-office',
                        link: '/admin/select-context',
                        subtitle: 'Choose org/region/location'
                    },
                    {
                        id: 'admin-ingestion',
                        title: '2. Data Ingestion',
                        type: 'basic',
                        icon: 'heroicons_outline:cloud-arrow-up',
                        link: '/admin/data-ingestion',
                        subtitle: 'Upload and filter ERP data'
                    },
                    {
                        id: 'admin-copper-ingestion',
                        title: '3. Copper Ingestion',
                        type: 'basic',
                        icon: 'heroicons_outline:cube',
                        link: '/admin/copper-ingestion',
                        subtitle: 'Map session to Copper layer'
                    },
                    {
                        id: 'admin-bronze-ingestion',
                        title: '4. Bronze Ingestion',
                        type: 'basic',
                        icon: 'heroicons_outline:document-duplicate',
                        link: '/admin/bronze-ingestion',
                        subtitle: 'Map Copper to Bronze ServiceLines'
                    },
                    {
                        id: 'admin-facility-classification',
                        title: '5. Facility Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:building-office-2',
                        link: '/admin/facility-classification-pipeline',
                        subtitle: 'Extract and classify facilities'
                    },
                    {
                        id: 'admin-payer-classification',
                        title: '6. Payer Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:identification',
                        link: '/admin/payer-classification-pipeline',
                        subtitle: 'Label insurance and product rollups'
                    },
                    {
                        id: 'admin-denial-classification',
                        title: '6a. Denial Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:x-circle',
                        link: '/admin/denial-classification-pipeline',
                        subtitle: 'Classify denial reason codes'
                    },
                    {
                        id: 'admin-diagnosis-classification',
                        title: '6b. Diagnosis Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:clipboard-document-list',
                        link: '/admin/diagnosis-classification-pipeline',
                        subtitle: 'Classify ICD-10 diagnosis codes'
                    },
                    {
                        id: 'admin-physician-classification',
                        title: '6c. Physician Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:user-group',
                        link: '/admin/physician-classification-pipeline',
                        subtitle: 'Classify and group physicians'
                    },
                    {
                        id: 'admin-adjustment-classification',
                        title: '6d. Adjustment Classification',
                        type: 'basic',
                        icon: 'heroicons_outline:adjustments-horizontal',
                        link: '/admin/adjustment-classification-pipeline',
                        subtitle: 'Classify adjustment event codes'
                    },
                    {
                        id: 'admin-cpt-enrichment',
                        title: '7a. CPT Enrichment',
                        type: 'basic',
                        icon: 'heroicons_outline:tag',
                        link: '/admin/cpt-enrichment',
                        subtitle: 'CPT label groups and per-code classifications'
                    },
                    {
                        id: 'admin-fee-schedule-management',
                        title: '7b. Fee Schedule Management',
                        type: 'basic',
                        icon: 'heroicons_outline:calculator',
                        link: '/admin/fee-schedule-management',
                        subtitle: 'Upload, browse, and report on payer fee schedules'
                    },
                    {
                        id: 'admin-medicare-rates',
                        title: '8. Medicare & Rates',
                        type: 'basic',
                        icon: 'heroicons_outline:currency-dollar',
                        link: '/admin/medicare-rates',
                        subtitle: 'Medicare enrichment and payer inference'
                    },
                    {
                        id: 'admin-silver-analytics',
                        title: '9. Silver Analytics',
                        type: 'basic',
                        icon: 'heroicons_outline:chart-bar',
                        link: '/admin/silver-analytics',
                        subtitle: 'Generate visit-level analytics'
                    },
                    {
                        id: 'admin-gold-aggregation',
                        title: '10. Gold Aggregation',
                        type: 'basic',
                        icon: 'heroicons_outline:chart-bar-square',
                        link: '/admin/gold-aggregation',
                        subtitle: 'Build pre-aggregated dashboard data'
                    },
                ]
            },

            // Ingestion Tools
            {
                id: 'ingestion-tools',
                title: 'Ingestion Tools',
                type: 'collapsable',
                icon: 'heroicons_outline:wrench-screwdriver',
                subtitle: 'Validation, cleanup & monitoring',
                children: [
                    {
                        id: 'admin-import-extracted',
                        title: 'Map to TMA',
                        type: 'basic',
                        icon: 'heroicons_outline:arrow-path-rounded-square',
                        link: '/admin/import-extracted-data',
                        subtitle: 'Import filtered data to TMA'
                    },
                    {
                        id: 'admin-encounter-analytics',
                        title: 'Visit Analytics',
                        type: 'basic',
                        icon: 'heroicons_outline:magnifying-glass-circle',
                        link: '/admin/encounter-analytics',
                        subtitle: 'Analyze visits by Visit ID'
                    },
                    {
                        id: 'admin-session-cpt-analysis',
                        title: 'Session CPT Analysis',
                        type: 'basic',
                        icon: 'heroicons_outline:document-chart-bar',
                        link: '/admin/session-cpt-analysis',
                        subtitle: 'Analyze CPT to Billing mappings'
                    },
                    {
                        id: 'admin-import-validation',
                        title: 'Import Validation',
                        type: 'basic',
                        icon: 'tma:quality-compliance',
                        link: '/admin/import-validation',
                        subtitle: 'Validate pipeline data integrity'
                    },
                    {
                        id: 'admin-data-cleanup',
                        title: 'Data Cleanup',
                        type: 'basic',
                        icon: 'heroicons_outline:trash',
                        link: '/admin/data-cleanup',
                        subtitle: 'Delete lakehouse records'
                    },
                    {
                        id: 'admin-audit',
                        title: 'Audit Logs',
                        type: 'basic',
                        icon: 'heroicons_outline:document-magnifying-glass',
                        link: '/admin/audit-logs',
                        subtitle: 'View activity history'
                    },
                    {
                        id: 'admin-orchestration-monitor',
                        title: 'Orchestration Monitor',
                        type: 'basic',
                        icon: 'heroicons_outline:server-stack',
                        link: '/admin/orchestration-monitor',
                        subtitle: 'Monitor & control running processes'
                    },
                    {
                        id: 'admin-ingestion-test',
                        title: 'Ingestion Test',
                        type: 'basic',
                        icon: 'heroicons_outline:beaker',
                        link: '/admin/ingestion-test',
                        subtitle: 'Silver vs CSV integrity check'
                    },
                ]
            },

            // External Ingestion Tools
            {
                id: 'external-ingestion-tools',
                title: 'External Ingestion Tools',
                type: 'collapsable',
                icon: 'heroicons_outline:cloud-arrow-down',
                subtitle: 'CMS and Medicare imports',
                children: [
                    {
                        id: 'admin-medicare-ingestion',
                        title: 'Medicare Ingestion',
                        type: 'basic',
                        icon: 'heroicons_outline:document-chart-bar',
                        link: '/admin/medicare-ingestion',
                        subtitle: 'Import CMS Medicare Fee Schedules'
                    },
                    {
                        id: 'admin-data-loader',
                        title: 'CMS Data Loader',
                        type: 'basic',
                        icon: 'heroicons_outline:circle-stack',
                        link: '/admin/data-loader',
                        subtitle: 'Import CMS medical codes'
                    },
                ]
            },
            {
                id: 'admin-try-query',
                title: 'Try Query',
                type: 'basic',
                icon: 'heroicons_outline:command-line',
                link: '/admin/try-query',
                subtitle: 'AI-powered Cosmos DB explorer'
            },
            // ─────────────────────────────────────────────────────────────
            // Project Constellation — entity meta-dashboards (schemaVersion 2)
            // Children added as each phase lands: Patient (1.1), Encounter (1.2),
            // CPT (1.3), Rendering Physician (1.4), Facility (1.5), Payer (1.6),
            // Diagnosis (1.7), Referring Physician (1.8), Service Line (1.9).
            // ─────────────────────────────────────────────────────────────
            {
                id: 'admin-tma-meta-dashboards',
                title: 'TMA Meta Dashboards',
                type: 'collapsable',
                icon: 'tma:triad-dashboard',
                subtitle: 'Practice + entity dashboards',
                children: this.buildMetaDashboardChildren('admin')
            },

            // ─────────────────────────────────────────────────────────────
            // Marketing site — public landing at /welcome. Pinned to bottom.
            // ─────────────────────────────────────────────────────────────
            {
                id: 'admin-marketing-site',
                title: 'Marketing Site',
                type: 'basic',
                icon: 'tma:tminsights',
                link: '/welcome',
                externalLink: false,
                target: '_blank',
                subtitle: 'Public landing page'
            }
        );

        return {
            id: 'admin',
            title: 'Admin',
            type: 'group',
            icon: 'tma:quality-compliance',
            subtitle: `Administration tools · ${APP_VERSION}`,
            children
        };
    }
}
